/**
 * Evaluation Completion — who is behind, by manager.
 *
 * Built so HR can nudge the manager rather than chase individual officers.
 * Worst first: the whole point of the screen is answering "who needs a push".
 * A manager opening it sees only their own row — handing them a league table of
 * their peers is not oversight.
 */
import React, { useEffect, useState } from 'react';
import { evaluationsAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import AccessState from '../components/auth/AccessState';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ChevronDown, ChevronRight, RefreshCw, Search, Users } from 'lucide-react';

const Stat = ({ label, value, tone = '' }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-fg-muted">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${tone}`}>{value}</p>
    </CardContent>
  </Card>
);

export default function EvaluationCompletion() {
  const canView = useCap('evaluations', 'view');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({});
  const [hideClear, setHideClear] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await evaluationsAPI.completionOverview();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load completion overview');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="eval-completion-page">
        <AccessState module="evaluations" action="view" items={[]} denied
          noAccessTitle="No access to evaluation completion"
          noAccessDescription="Ask an administrator if you need to see evaluation progress." />
      </div>
    );
  }

  const rows = (data?.managers || []).filter(m => {
    if (hideClear && !m.overdue && !m.stuck && !m.due_soon) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${m.name} ${m.email}`.toLowerCase().includes(q);
  });
  const t = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="eval-completion-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight">Evaluation Completion</h1>
          <p className="text-base text-fg-muted mt-1 max-w-2xl">
            Outstanding evaluations by manager, worst first. "Unsigned" counts anything sitting
            more than {data?.stale_after_days ?? 14} days waiting on a signature.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="eval-completion-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Overdue" value={t.overdue ?? 0}
          tone={t.overdue ? 'text-red-600' : 'text-emerald-600'} />
        <Stat label="Due soon" value={t.due_soon ?? 0} />
        <Stat label="Unsigned" value={t.stuck ?? 0}
          tone={t.stuck ? 'text-amber-600' : ''} />
        <Stat label="Completed this year" value={t.completed_this_year ?? 0}
          tone="text-emerald-600" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
          <Input className="pl-9" placeholder="Manager name or email…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="eval-completion-search" />
        </div>
        <Button variant={hideClear ? 'default' : 'outline'} size="sm"
          onClick={() => setHideClear(v => !v)} data-testid="eval-completion-toggle-clear">
          {hideClear ? 'Showing only managers with work' : 'Showing every manager'}
        </Button>
        <span className="text-xs text-fg-muted">
          {t.managers_with_team ?? 0} managers supervise {t.team_members ?? 0} people
        </span>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <p className="p-6 text-sm text-fg-muted">Loading…</p>
          ) : !rows.length ? (
            <div className="p-10 text-center" data-testid="eval-completion-empty">
              <Users className="w-8 h-8 mx-auto text-fg-muted mb-3" />
              <p className="text-sm text-fg-muted">
                {hideClear ? 'Nobody is behind on evaluations.' : 'No managers match that search.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-right">Team</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Due soon</TableHead>
                  <TableHead className="text-right">Unsigned</TableHead>
                  <TableHead className="text-right">Done this year</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(m => (
                  <React.Fragment key={m.user_id}>
                    <TableRow
                      className={m.items?.length ? 'cursor-pointer' : ''}
                      onClick={() => m.items?.length
                        && setOpen(o => ({ ...o, [m.user_id]: !o[m.user_id] }))}
                      data-testid={`eval-completion-row-${m.user_id}`}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          {m.items?.length
                            ? (open[m.user_id] ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />)
                            : <span className="w-4" />}
                          <span className="truncate">{m.name || m.email}</span>
                          {m.unlinked && (
                            <Badge variant="outline" className="text-xs">no team</Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{m.team_size}</TableCell>
                      <TableCell className="text-right">
                        {m.overdue
                          ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{m.overdue}</Badge>
                          : <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell className="text-right">{m.due_soon || '—'}</TableCell>
                      <TableCell className="text-right">
                        {m.stuck
                          ? <Badge variant="outline" className="border-amber-300 text-amber-700">{m.stuck}</Badge>
                          : <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell className="text-right">{m.completed_this_year}</TableCell>
                    </TableRow>
                    {open[m.user_id] && (m.items || []).map((it, idx) => (
                      <TableRow key={`${m.user_id}-${idx}`} className="bg-surface-sunken">
                        <TableCell colSpan={6} className="text-sm">
                          <span className="pl-7 text-fg-muted">
                            <b className="text-fg">{it.employee_name}</b> · {it.evaluation_type} ·
                            due {it.target_date} · {it.days_overdue} day
                            {it.days_overdue === 1 ? '' : 's'} overdue
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
